import { normalizePhoneDigits, phoneDigitsMatchSamePerson } from './phoneContactMatch';

/** Mirrors `kMinDisplayNameLength` / `kMaxDisplayNameLength` in `lib/utils/display_name_validation.dart`. */
export const MIN_DISPLAY_NAME_LENGTH = 3;
export const MAX_DISPLAY_NAME_LENGTH = 20;

/** Letters, spaces, and hyphens only — no numbers or other symbols. */
const DISPLAY_NAME_PATTERN = /^[a-zA-Z\s\-]+$/;

export const DISPLAY_NAME_HELPER_TEXT =
  'Your display name will be visible to your team in tasks, chat, and reports. You can change it later.';

export const DISPLAY_NAME_RULES_HINT =
  '3–20 characters. Use only letters, spaces, and hyphens (no numbers).';

/** True when [name] contains characters outside the allowed display-name alphabet. */
export function hasInvalidDisplayNameCharacters(name: string): boolean {
  return /[^a-zA-Z\s\-]/.test(name);
}

/**
 * Mirrors `isValidDisplayName` on mobile (length + pattern only).
 * For submit/save flows, prefer [displayNameValidationError].
 */
export function isValidDisplayName(name: string): boolean {
  const trimmed = name.trim();
  if (trimmed.length < MIN_DISPLAY_NAME_LENGTH) return false;
  if (trimmed.length > MAX_DISPLAY_NAME_LENGTH) return false;
  return DISPLAY_NAME_PATTERN.test(trimmed);
}

/** Mirrors `displayNameValidationError` on mobile. */
export function displayNameValidationError(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return 'Display name is required.';
  if (trimmed.length < MIN_DISPLAY_NAME_LENGTH) {
    return `Display name must be at least ${MIN_DISPLAY_NAME_LENGTH} characters.`;
  }
  if (trimmed.length > MAX_DISPLAY_NAME_LENGTH) {
    return `Display name must be ${MAX_DISPLAY_NAME_LENGTH} characters or fewer.`;
  }
  if (!DISPLAY_NAME_PATTERN.test(trimmed)) {
    return 'Use only letters, spaces, and hyphens.';
  }
  if (isPhoneLikeDisplayName(trimmed)) {
    return 'Enter your name, not a phone number.';
  }
  return null;
}

/** Mirrors `isPhoneLikeDisplayName` on mobile. */
export function isPhoneLikeDisplayName(
  displayName?: string,
  memberPhone?: string,
): boolean {
  const dn = displayName?.trim();
  if (!dn) return false;
  const dDigits = normalizePhoneDigits(dn);
  if (dDigits.length < 10) return false;
  const pDigits = normalizePhoneDigits(memberPhone);
  return pDigits.length === 0 || phoneDigitsMatchSamePerson(dDigits, pDigits);
}

/** Mirrors `isUsablePrefillDisplayName` on mobile. */
export function isUsablePrefillDisplayName(
  displayName?: string,
  memberPhone?: string,
): boolean {
  const dn = displayName?.trim();
  if (!dn) return false;
  if (isPhoneLikeDisplayName(dn, memberPhone)) return false;
  return isValidDisplayName(dn);
}

/**
 * Render a phone-derived id/field as a full "+<country><number>" label.
 * Member uids are the msisdn digits (e.g. 919876543210); stored phoneNumber is E.164.
 * Use as the report fallback instead of truncating the uid with slice(0, 8).
 */
export function phoneLabel(raw?: string): string {
  const s = (raw ?? '').trim();
  if (!s) return '';
  if (s.startsWith('+')) return s; // already E.164
  if (/^\d{8,15}$/.test(s)) return `+${s}`; // digits-only msisdn -> add country-code prefix
  return s;
}

/** Mirrors `memberDisplayLabel` on mobile — no contact-book lookup. */
export function memberDisplayLabel(member: {
  uid: string;
  displayName?: string;
  phoneNumber?: string;
}): string {
  const dn = member.displayName?.trim();
  if (dn) return dn;
  const phone = member.phoneNumber?.trim();
  if (phone) return phone;
  return member.uid;
}
